import { HotsauceProvider } from '@hotjar/hotsauce';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { useTranslation } from 'react-i18next';
import { BrowserRouter } from 'react-router-dom';
import { queryClient } from '../queryClient';
import { getAnalytics } from '../observability';
import { Analytics } from '../observability/segment.types';

export interface GlobalModalProps<TSubmit, TCancel = Error> {
  onSubmit(data?: TSubmit): void;

  onCancel(error?: TCancel): void;
}

type TrackingOptions = {
  ui_position: string;
  modal_name: string;
  modal_version?: string;
};

function Providers({ children }: { children: JSX.Element }) {
  const { i18n, ready } = useTranslation('engage');

  return (
    <HotsauceProvider
      i18n={{
        language: i18n.resolvedLanguage ?? 'en',
        strings: ready ? i18n.getResourceBundle(i18n.resolvedLanguage ?? 'en', 'hotsauce') : {},
      }}
    >
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <Suspense fallback={null}>{children}</Suspense>
        </BrowserRouter>
      </QueryClientProvider>
    </HotsauceProvider>
  );
}

export function createGlobalModal<TProps, TSubmit, TCancel = Error, TOptions = void>(
  Modal: (props: TProps & GlobalModalProps<TSubmit, TCancel> & TOptions) => JSX.Element | null,
  trackOptions?: TrackingOptions,
): (props?: TProps, trackingOverrides?: TrackingOptions) => Promise<TSubmit> {
  const root = document.getElementsByTagName('body')[0];

  return (props?: TProps, trackingOverrides?: TrackingOptions): Promise<TSubmit> => {
    return new Promise<TSubmit>((resolve) => {
      const container = document.createElement('div');
      const reactRoot = createRoot(container);
      const analytics = getAnalytics();
      const trackingOptions = trackingOverrides || trackOptions;

      const destroyContainer = (analytics: Analytics, trackingOptions?: TrackingOptions) => {
        reactRoot.unmount();
        root?.removeChild(container);
        if (analytics && trackingOptions) {
          analytics.track('Modal Closed', {
            modal_name: trackingOptions?.modal_name,
            modal_version: trackingOptions?.modal_version,
          });
        }
      };

      const onSubmit = (data: TSubmit) => {
        if (analytics && trackingOptions) {
          analytics.track('Element Clicked', {
            element_name: 'Submit',
            ui_position: trackingOptions.ui_position,
          });
        }
        resolve(data);
        destroyContainer(analytics, trackingOptions);
      };

      const onCancel = () => {
        if (analytics && trackingOptions) {
          analytics.track('Element Clicked', {
            element_name: 'Cancel',
            ui_position: trackingOptions.ui_position,
          });
        }

        destroyContainer(analytics, trackingOptions);
      };

      root?.appendChild(container);

      if (analytics && trackingOptions) {
        analytics.track('Modal Loaded', {
          modal_name: trackingOptions.modal_name,
          modal_version: trackingOptions.modal_version,
        });
      }

      reactRoot.render(
        <StrictMode>
          <Providers>
            <Modal {...(props as TProps & TOptions)} onCancel={onCancel} onSubmit={onSubmit} />
          </Providers>
        </StrictMode>,
      );
    });
  };
}
